import { ContextConsumer } from '@lit/context'
import * as closeIcon from '@nordhealth/icons/lib/assets/interface-close-small.js'
import { isServer, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import Icon from '../icon/Icon.js'
import {
  fallback,
  resolveTranslation,
  subscribeToDocumentLang,
  subscribeToTranslationRegistration,
} from '../localization/translation.js'
import { drawerContext } from './drawer-context.js'
import style from './parts.css'

Icon.registerIcon(closeIcon)

/**
 * A standalone close control for a composed [Drawer](/components/drawer/).
 *
 * When left empty it renders a default close icon button. Give it your own
 * control and it becomes a transparent wrapper that removes the nearest
 * `nord-drawer` when clicked.
 *
 * @status draft
 * @category structure
 * @lightDom true
 *
 * @slot - Optional custom close control. When empty, a default icon button is rendered.
 */
@customElement('nord-drawer-close')
export default class DrawerClose extends DraftComponentMixin(LitElement) {
  /** Overrides the accessible label of the default close button. */
  @property() label?: string

  private defaultButton?: HTMLButtonElement
  private unsubscribeLang?: () => void
  private unsubscribeTranslationRegistration?: () => void
  private langObserver?: MutationObserver
  private drawer = new ContextConsumer(this, {
    context: drawerContext,
    subscribe: true,
  })

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style, 'nord-drawer-parts')
    this.addEventListener('click', this.handleClick)
    this.unsubscribeLang = subscribeToDocumentLang(this.handleLanguageChange)
    this.unsubscribeTranslationRegistration = subscribeToTranslationRegistration(this.handleLanguageChange)
    if (!isServer) {
      this.langObserver = new MutationObserver(this.handleLanguageChange)
      this.langObserver.observe(this, { attributes: true, attributeFilter: ['lang'] })
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('click', this.handleClick)
    this.unsubscribeLang?.()
    this.unsubscribeTranslationRegistration?.()
    this.langObserver?.disconnect()
    this.unsubscribeLang = undefined
    this.unsubscribeTranslationRegistration = undefined
    this.langObserver = undefined
  }

  protected createRenderRoot() {
    return this
  }

  protected firstUpdated() {
    if (this.childElementCount === 0) {
      this.defaultButton = this.createDefaultButton()
      this.appendChild(this.defaultButton)
    }
  }

  protected updated() {
    this.defaultButton?.querySelector('nord-icon')?.setAttribute('label', this.resolvedLabel)
  }

  private get resolvedLabel(): string {
    if (this.label) {
      return this.label
    }
    const lang = this.lang || (isServer ? 'en-US' : document.documentElement.lang)
    const translation = resolveTranslation(lang)
    return translation['nord-drawer']?.closeLabel ?? fallback['nord-drawer'].closeLabel
  }

  private createDefaultButton() {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'n-drawer-close'

    const icon = document.createElement('nord-icon')
    icon.setAttribute('name', 'interface-close-small')
    icon.setAttribute('size', 's')
    icon.setAttribute('label', this.resolvedLabel)
    button.appendChild(icon)

    return button
  }

  private handleLanguageChange = () => {
    this.requestUpdate()
  }

  private handleClick = (event: Event) => {
    this.drawer.value?.requestClose(event)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-drawer-close': DrawerClose
  }
}
